import { useState, useEffect } from 'react';
import { FaSave, FaEdit, FaTrash, FaSpinner, FaPlus, FaGripVertical } from 'react-icons/fa';
import { usePortfolio } from '../../context/PortfolioContext';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { uploadAPI } from '../../utils/api.js';
import VisibilityToggle from './VisibilityToggle';
import { API_BASE_URL } from '../../config/apiConfig';

const emptyEducation = {
  degree: '',
  institution: '',
  location: '',
  year: '',
  description: '',
  image_url: '',
  visible: true
};

const EducationEditor = () => {
  const { portfolioData, updatePortfolioData } = usePortfolio();
  const [education, setEducation] = useState(
    (portfolioData?.education || []).map((e, i) => ({ ...e, order: e.order ?? i }))
  );
  const [form, setForm] = useState(emptyEducation);
  const [editingIndex, setEditingIndex] = useState(null);
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [message, setMessage] = useState('');
  const [toggleLoading, setToggleLoading] = useState({});

  useEffect(() => {
    // Always fetch all education entries for admin
    fetch(`${API_BASE_URL}/education?all=true`)
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setEducation((data.data || []).map((e, i) => ({ ...e, order: e.order ?? i })));
        }
      });
  }, []);

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleEdit = (edu, idx) => {
    setForm(edu);
    setEditingIndex(idx);
    setError('');
    setMessage('');
  };

  const handleCancel = () => {
    setForm(emptyEducation);
    setEditingIndex(null);
    setError('');
    setMessage('');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setMessage('');
    if (!form.degree || !form.institution || !form.year) {
      setError('Degree, institution, and year are required.');
      return;
    }
    setIsSaving(true);
    try {
      const newItem = {
        ...form,
        id: form.id || (editingIndex !== null ? education[editingIndex].id : Date.now()),
        visible: form.visible
      };
      await updatePortfolioData('education', newItem);
    setForm(emptyEducation);
    setEditingIndex(null);
      setMessage('Education entry saved!');
    } catch (err) {
      setError('Error saving education entry.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this education entry?')) return;
    setIsDeleting(true);
    setError('');
    setMessage('');
    try {
      await updatePortfolioData('education-delete', {}, id);
      setMessage('Education entry deleted!');
      if (editingIndex !== null) handleCancel();
    } catch (err) {
      setError('Error deleting education entry.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Drag-and-drop handlers
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const reordered = Array.from(education);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    // Update order field for each entry
    setEducation(reordered.map((e, i) => ({ ...e, order: i })));
  };

  const handleToggleVisibility = async (index, checked) => {
    const edu = education[index];
    setToggleLoading(prev => ({ ...prev, [edu.id]: true }));
    try {
      const updatedEdu = { ...edu, visible: checked, id: edu.id };
      await updatePortfolioData('education', updatedEdu);
      setEducation(education.map((e, i) => i === index ? { ...e, visible: checked } : e));
    } catch (error) {
      // Optionally show error
    } finally {
      setToggleLoading(prev => ({ ...prev, [edu.id]: false }));
    }
  };

  // Add this function for toggling visibility in the education list
  const handleEducationChange = (index, field, value) => {
    const updatedItems = [...education];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value,
    };
    setEducation(updatedItems);
  };

  return (
    <div className="max-w-3xl mx-auto p-6 bg-gray-900 rounded-2xl shadow-lg">
      <h3 className="text-2xl font-bold text-primary mb-6">Manage Education</h3>
      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4 mb-8">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Degree</label>
        <input
              type="text"
          name="degree"
          value={form.degree}
          onChange={handleChange}
              className="w-full px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="e.g. B.E. Computer Science"
        />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Institution</label>
        <input
              type="text"
          name="institution"
          value={form.institution}
          onChange={handleChange}
              className="w-full px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="e.g. RNSIT"
        />
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Location</label>
        <input
              type="text"
          name="location"
          value={form.location}
          onChange={handleChange}
              className="w-full px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="e.g. Bangalore"
        />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Year</label>
        <input
              type="text"
          name="year"
          value={form.year}
          onChange={handleChange}
              className="w-full px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="e.g. 2022"
        />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
        <textarea
          name="description"
          value={form.description}
          onChange={handleChange}
            rows={3}
            className="w-full px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-primary resize-vertical"
            placeholder="Describe your education..."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Image (optional)</label>
          {form.image_url && (
            <div className="mb-2 flex items-center gap-2">
              <img src={form.image_url} alt="Education" className="h-20 rounded border border-gray-700" />
              <button type="button" onClick={() => setForm(prev => ({ ...prev, image_url: '' }))} className="text-red-400 hover:text-red-600 text-xs">Remove</button>
            </div>
          )}
          <input
            type="file"
            accept="image/*"
            className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary/20 file:text-primary hover:file:bg-primary/30"
            onChange={async (e) => {
              const file = e.target.files[0];
              if (!file) return;
              setError('');
              setMessage('');
              try {
                const response = await uploadAPI.uploadImage(file);
                if (response.success) {
                  setForm(prev => ({ ...prev, image_url: response.data.url }));
                  setMessage('Image uploaded!');
                } else {
                  setError(response.error || 'Failed to upload image');
                }
              } catch (err) {
                setError('Error uploading image');
              }
            }}
          />
        </div>
        <VisibilityToggle
            checked={form.visible}
            onChange={e => setForm({ ...form, visible: e.target.checked })}
          loading={false}
          />
        {error && <div className="text-red-400 text-sm">{error}</div>}
        {message && <div className="text-green-400 text-sm">{message}</div>}
        <div className="flex gap-4 justify-end">
          <button
            type="submit"
            className="px-6 py-2 bg-primary text-white rounded-lg flex items-center gap-2 hover:bg-primary-dark disabled:opacity-50"
            disabled={isSaving}
          >
            {isSaving ? <FaSpinner className="animate-spin" /> : (editingIndex !== null ? <FaEdit /> : <FaPlus />)}
            {isSaving ? 'Saving...' : (editingIndex !== null ? 'Update' : 'Add')}
          </button>
          {editingIndex !== null && (
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800"
            >
              Cancel
            </button>
          )}
        </div>
      </form>
      {/* List with drag-and-drop */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="education-list">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef} className="grid gap-6">
        {education.map((edu, idx) => (
                <Draggable key={edu.id} draggableId={String(edu.id)} index={idx}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={`bg-gray-800 rounded-xl p-4 flex items-center gap-4 shadow transition-all ${snapshot.isDragging ? 'ring-2 ring-primary scale-105' : ''}`}
                    >
                      <span {...provided.dragHandleProps} className="cursor-grab text-gray-400 hover:text-primary mr-2"><FaGripVertical /></span>
                      <div className="flex-1">
                        <div className="font-bold text-white text-lg">{edu.degree}</div>
                        <div className="text-primary font-semibold">{edu.institution}</div>
                        <div className="text-gray-400 text-sm">{edu.year} • {edu.location}</div>
                        <div className="text-gray-300 text-sm whitespace-pre-line mt-1">{edu.description}</div>
                        {edu.image_url && (
                          <img src={edu.image_url} alt="Education" className="mt-2 h-16 rounded border border-gray-700" />
                        )}
                      </div>
                      <VisibilityToggle
                        checked={edu.visible ?? true}
                        onChange={e => handleEducationChange(idx, 'visible', e.target.checked)}
                        loading={!!toggleLoading[edu.id]}
                      />
                      <button
                        onClick={() => handleEdit(edu, idx)}
                        className="px-3 py-1 bg-blue-700 text-white rounded hover:bg-blue-800 flex items-center gap-1"
                        disabled={isSaving || isDeleting}
                      >
                        <FaEdit /> Edit
                      </button>
                      <button
                        onClick={() => handleDelete(edu.id)}
                        className="px-3 py-1 bg-red-700 text-white rounded hover:bg-red-800 flex items-center gap-1"
                        disabled={isSaving || isDeleting}
                      >
                        {isDeleting ? <FaSpinner className="animate-spin" /> : <FaTrash />} Delete
                      </button>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

export default EducationEditor; 